import { findMeaningById, findWordById } from "./grouping.ts";
import type { MeaningCard, MeaningRelation, VocabularySnapshot } from "./types.ts";

export type DuplicateMatchKind = "saved" | "uncertain" | "distinct";

export interface DuplicateMatch {
  kind: DuplicateMatchKind;
  existingMeaningId: string;
}

export function validateReturnedMeaningId(
  snapshot: VocabularySnapshot,
  meaningId: string,
  expectedWordId?: string,
): MeaningCard | undefined {
  const meaning = findMeaningById(snapshot, meaningId);
  if (!meaning) {
    return undefined;
  }
  if (expectedWordId && meaning.wordId !== expectedWordId) {
    return undefined;
  }
  return meaning;
}

export function relationKey(sourceMeaningId: string, targetMeaningId: string): string {
  return `${sourceMeaningId}→${targetMeaningId}`;
}

export function canCreateRelation(
  snapshot: VocabularySnapshot,
  sourceMeaningId: string,
  targetMeaningId: string,
): { ok: true } | { ok: false; reason: "self" | "missing" | "duplicate" } {
  if (sourceMeaningId === targetMeaningId) {
    return { ok: false, reason: "self" };
  }
  if (!findMeaningById(snapshot, sourceMeaningId) || !findMeaningById(snapshot, targetMeaningId)) {
    return { ok: false, reason: "missing" };
  }
  const exists = snapshot.relations.some(
    (relation) =>
      relation.sourceMeaningId === sourceMeaningId && relation.targetMeaningId === targetMeaningId,
  );
  if (exists) {
    return { ok: false, reason: "duplicate" };
  }
  return { ok: true };
}

export function relationsFrom(
  snapshot: VocabularySnapshot,
  sourceMeaningId: string,
): MeaningRelation[] {
  return snapshot.relations.filter((relation) => relation.sourceMeaningId === sourceMeaningId);
}

export function relationsInvolving(
  snapshot: VocabularySnapshot,
  meaningId: string,
): MeaningRelation[] {
  return snapshot.relations.filter(
    (relation) => relation.sourceMeaningId === meaningId || relation.targetMeaningId === meaningId,
  );
}

export function relationTargetLabel(
  snapshot: VocabularySnapshot,
  targetMeaningId: string,
): string | undefined {
  const meaning = findMeaningById(snapshot, targetMeaningId);
  if (!meaning) {
    return undefined;
  }
  const word = findWordById(snapshot, meaning.wordId);
  if (!word) {
    return undefined;
  }
  return `${word.displayText} · ${meaning.shortLabel}`;
}

export function hasValidOutgoingRelation(
  snapshot: VocabularySnapshot,
  meaningId: string,
): boolean {
  return snapshot.relations.some((relation) => {
    if (relation.sourceMeaningId !== meaningId) {
      return false;
    }
    return Boolean(findMeaningById(snapshot, relation.targetMeaningId));
  });
}
